<template>
  <v-card
    v-if="uploadEntries.length > 0"
    class="cc-upload-bar"
    elevation="8"
    data-test="upload-bar"
    role="region"
    :aria-label="title"
  >
    <v-card-title class="d-flex align-center ga-2 py-2 bg-primary">
      <span class="text-subtitle-1 flex-grow-1">{{ title }}</span>
      <v-btn
        variant="text"
        size="small"
        :icon="collapsed ? Icons.CHEVRON_UP : Icons.CHEVRON_DOWN"
        :aria-label="collapsed ? t('fileSystem.upload.bar.expand') : t('fileSystem.upload.bar.collapse')"
        @click="collapsed = !collapsed"
      />
      <v-btn
        v-if="!hasActiveUploads"
        variant="text"
        size="small"
        :icon="Icons.CLOSE"
        :aria-label="t('fileSystem.upload.bar.close')"
        @click="clearFinishedUploads()"
      />
    </v-card-title>
    <template v-if="!collapsed">
      <v-alert
        v-if="queuePaused"
        type="warning"
        density="compact"
        rounded="0"
        :text="t('fileSystem.upload.bar.paused')"
      />
      <v-list
        density="compact"
        class="cc-upload-bar__list py-0"
      >
        <v-list-item
          v-for="entry in uploadEntries"
          :key="entry.id"
          :data-status="entry.status"
        >
          <v-list-item-title class="text-body-2">
            {{ entry.file.name }}
          </v-list-item-title>
          <v-list-item-subtitle class="d-flex ga-2">
            <span>{{ statusText(entry) }}</span>
            <span v-if="entry.status === 'uploading' && entry.remainingSeconds !== null">{{ remainingText(entry.remainingSeconds) }}</span>
          </v-list-item-subtitle>
          <v-progress-linear
            v-if="entry.status === 'uploading' || entry.status === 'processing'"
            class="mt-1"
            color="primary"
            :model-value="entry.progress"
            :indeterminate="entry.status === 'processing'"
          />
          <template #append>
            <v-icon
              v-if="entry.status === 'done'"
              color="success"
              :icon="Icons.CHECK"
            />
            <v-btn
              v-else-if="entry.status === 'waiting' || entry.status === 'uploading'"
              variant="text"
              size="small"
              :icon="Icons.CLOSE"
              :aria-label="t('fileSystem.upload.bar.cancel', {name: entry.file.name})"
              @click="cancelUpload(entry.id)"
            />
            <v-btn
              v-else-if="entry.status !== 'processing'"
              variant="text"
              size="small"
              :icon="Icons.REFRESH"
              :aria-label="t('fileSystem.upload.bar.retry', {name: entry.file.name})"
              @click="retryUpload(entry.id)"
            />
          </template>
        </v-list-item>
      </v-list>
    </template>
  </v-card>
</template>

<script setup lang="ts">
//================= [CSS] =====================================

//================= [JS] ======================================
import {
  cancelUpload,
  clearFinishedUploads,
  hasActiveUploads,
  queuePaused,
  retryUpload,
  UploadEntry,
  uploadEntries,
} from '@/composables/fileSystem/uploadQueue';
import {warnBeforeUnload} from '@/composables/fileSystem/uploadGuard';
import {Icons} from '@simpleforyou/client-core';
import {computed, onBeforeUnmount, onMounted, ref, watch} from 'vue';
import {useI18n} from 'vue-i18n';
//================= [VUE COMPONENTS] ==========================

// T00V7O2QY41Z — upload bar bottom right, in the main layout so it stays
// across page changes. Disappears on its own once everything went through.
const {t} = useI18n();

// After the last upload finished without problems, the bar stays this long.
const AUTO_HIDE_MS = 4000;

const collapsed = ref(false);
const title = computed(() => t('fileSystem.upload.bar.title', uploadEntries.value.length));

const statusText = (entry: UploadEntry): string => t(`fileSystem.upload.status.${entry.status}`, {progress: entry.progress});

const remainingText = (seconds: number): string => {
  if (seconds < 60) {
    return t('fileSystem.upload.remaining.seconds', {count: seconds});
  }
  if (seconds < 3600) {
    return t('fileSystem.upload.remaining.minutes', {count: Math.ceil(seconds / 60)});
  }
  return t('fileSystem.upload.remaining.hours', {count: Math.round(seconds / 360) / 10});
};

let hideTimer: ReturnType<typeof setTimeout> | undefined;
const allSucceeded = computed(() => uploadEntries.value.length > 0 && uploadEntries.value.every(entry => entry.status === 'done'));

watch(allSucceeded, (succeeded) => {
  clearTimeout(hideTimer);
  if (succeeded) {
    hideTimer = setTimeout(() => clearFinishedUploads(), AUTO_HIDE_MS);
  }
});

// T00TNZLQQPYX — the bar lives in the main layout for the whole session, so it
// guards the tab against closing while uploads run.
onMounted(() => window.addEventListener('beforeunload', warnBeforeUnload));

onBeforeUnmount(() => {
  clearTimeout(hideTimer);
  window.removeEventListener('beforeunload', warnBeforeUnload);
});
</script>

<style scoped>
.cc-upload-bar {
  position: fixed;
  right: 16px;
  bottom: 16px;
  width: 360px;
  max-width: calc(100vw - 32px);
  z-index: 2000;
}

.cc-upload-bar__list {
  max-height: 320px;
  overflow-y: auto;
}
</style>
